/**
 * Sync Stage Labels
 *
 * The backend import-progress reporter emits machine stage slugs in
 * `metadata.stage` ('connection_established', 'fetching_endpoints', …) and
 * `formatBroadcasterSyncEvent` forwards them as `currentStage`. The SAME
 * field also carries free-form human-readable progress messages, so only
 * the known slugs are translated here — anything else passes through
 * verbatim (never key-mangled).
 *
 * Used by ImportProgressIndicator and SyncHistoryTable; the translations
 * live in the 'imports' namespace under `progressIndicator.stages.*`
 * (backend locales en + es).
 */

import type { TFunction } from '@platform/i18n/hooks/useTranslation'

// Known machine slugs → English default labels. The default doubles as the
// i18next fallback for the brief window before the 'imports' namespace has
// loaded over HTTP.
const SYNC_STAGE_SLUG_DEFAULT_LABELS: Record<string, string> = {
  connection_established: 'Connection established',
  fetching_endpoints: 'Fetching data',
  creating_transactions: 'Creating transactions',
  ledger_write: 'Writing to ledger',
  finalization: 'Finalizing',
  // Wallet-import checkpoint stages (`wallet-import-job.types.ts`) and the
  // terminal slugs every import leaves behind. Medido el 2026-09-24 en
  // `bat_e2e`: 999 trabajos con `stage = 'completed'`, 99 `acquisition`, 71
  // `failed`, 31 `init` — todos se pintaban en inglés crudo en la interfaz
  // española del historial de sincronización.
  init: 'Preparing',
  acquisition: 'Reading the chain',
  classification: 'Classifying',
  persistence: 'Saving',
  job_execution: 'Running',
  // A provider's rate limit holds the job; it resumes by itself (never a bug to report).
  waiting_provider: 'Waiting for {{provider}} (request limit) — resumes by itself at {{time}}',
  completed: 'Completed',
  failed: 'Failed',
}

/**
 * Terminal slugs reuse the job-STATUS labels the same namespace already ships
 * (`syncHistory.statuses.*`): the `imports` catalogue has a size ceiling
 * (`imports-no-dead-mapping-block.i18n.test.ts`) and «Completado» twice would
 * be two copies of one fact to keep in sync.
 */
const SYNC_STAGE_KEY_OVERRIDES: Readonly<Record<string, string>> = {
  completed: 'syncHistory.statuses.completed',
  failed: 'syncHistory.statuses.failed',
}

/**
 * Translate a known sync stage slug; pass unknown strings through verbatim.
 * `t` MUST come from `useTranslation('imports')` — the keys live in the
 * 'imports' namespace.
 */
export function translateSyncStageLabel(
  stage: string,
  t: TFunction,
  params: Readonly<Record<string, string>> = {},
): string {
  // A stage is text the SERVER wrote: `constructor` or `__proto__` must not find a label in the prototype.
  const defaultLabel = Object.hasOwn(SYNC_STAGE_SLUG_DEFAULT_LABELS, stage)
    ? SYNC_STAGE_SLUG_DEFAULT_LABELS[stage]
    : undefined
  if (defaultLabel === undefined) return stage
  const key = SYNC_STAGE_KEY_OVERRIDES[stage] ?? `progressIndicator.stages.${stage}`
  // interpolation values only for the stages that take them (waiting_provider)
  return Object.keys(params).length > 0 && /\{\{/.test(defaultLabel)
    ? t(key, { defaultValue: defaultLabel, ...params })
    : t(key, defaultLabel)
}

/** Un trabajo que ya no avanza: su etapa sólo repetiría el estado. */
export function isFinishedSyncStatus(status: string): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled'
}
